import React, { useRef } from 'react';
import PropTypes from 'prop-types';

import DefaultPicture, { propTypes as picturePropTypes } from '../../../../01_atoms/DefaultPicture';
import BulletPointBackgroundIcon from '../../../../../public/static/icons/bullet-point-background.svg';
import useOnScrollAnimation from '../../../../../utils/animationImageOnScroll';
import ResourceDownload from '../../../../02_molecules/ResourceDownload';
import FormattedText from '../../../../01_atoms/FormattedText';

import styles from './index.module.scss';

// The values for the first and last frames of animation.
const animationConfig = {
  1: {
    angleInitial: 7.57,
    angleFinished: 10,
    yInitial: 187,
    yFinished: 0,
    xInitial: 20,
    xFinished: 0,
    angleInitialMob: 7.57,
    angleFinishedMob: 10,
    yInitialMob: 88,
    yFinishedMob: 0,
    breakpoint: 992,
  },
  2: {
    angleInitial: -10,
    angleFinished: -8.7,
    yInitial: 171,
    yFinished: 0,
    xInitial: -35,
    xFinished: 0,
    angleInitialMob: -6.33,
    angleFinishedMob: -9.36,
    yInitialMob: 77,
    yFinishedMob: 0,
    breakpoint: 992,
  },
  3: {
    angleInitial: 12.57,
    angleFinished: 15,
    yInitial: 160,
    yFinished: 0,
    xInitial: 0,
    xFinished: 0,
    angleInitialMob: 12.57,
    angleFinishedMob: 15,
    yInitialMob: 66,
    yFinishedMob: 0,
    breakpoint: 992,
  },
  4: {
    angleInitial: -9.4,
    angleFinished: -6.91,
    yInitial: 188,
    yFinished: 0,
    xInitial: 5,
    xFinished: 0,
    angleInitialMob: -9.5,
    angleFinishedMob: -6.91,
    yInitialMob: 76,
    yFinishedMob: 0,
    breakpoint: 992,
  },
};

const ActivismResource = ({ number, image, title, description = '', resources = [] }) => {
  const stepType = ((number - 1) % 4) + 1;
  const refImage = useRef(null);
  const [animationTransformStyle] = useOnScrollAnimation(refImage, animationConfig[stepType]);

  return (
    <div className={`${styles['activism-resource']} ${styles[`step--type-${stepType}`]}`}>
      <div className={styles['activism-resource__content']}>
        <div className={styles['activism-resource__image-group']}>
          <div
            className={styles['activism-resource__image']}
            ref={refImage}
            style={animationTransformStyle}
          >
            <DefaultPicture {...image} />
          </div>
        </div>
        <div className={styles['activism-resource__info-group']}>
          <div className={styles['activism-resource__info']}>
            <div className={styles['activism-resource__number']}>
              <BulletPointBackgroundIcon className={styles['activism-resource__number-bg']} />
              {number}
            </div>
            <h3 className={styles['activism-resource__title']}>{title}</h3>

            {description && (
              <FormattedText
                className={styles['activism-resource__description']}
                text={description}
              />
            )}
          </div>
          <div className={styles['activism-resource__resources-container']}>
            {resources.map((resource, i) => (
              <ResourceDownload {...resource} key={`resource-download-${i}`} variant="activism" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

ActivismResource.propTypes = {
  image: PropTypes.shape(picturePropTypes).isRequired,
  number: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  resources: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      downloadURL: PropTypes.string.isRequired,
      info: PropTypes.string,
      publishedDate: PropTypes.string,
      actionIcon: PropTypes.oneOf(['download', 'tab']),
      format: PropTypes.oneOf([
        'PDF',
        'DOC',
        'DOCX',
        'XLSX',
        'XLS',
        'PPTX',
        'PPT',
        'ZIP',
        'AVI',
        'MP4',
        'MP3',
        'TXT',
        'undefined',
      ]),
    }),
  ),
};

export default ActivismResource;
